import React from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import AdminSignIn from "../../features/auth/AdminSignIn";
import ForgotPassword from "../../features/auth/ForgotPassword";
import PersistLogin from "../../features/auth/PersistLogin";
import RequireAuth from "../../features/auth/RequireAuth";
import ResetPassword from "../../features/auth/ResetPassword";
import BrandDashboard from "../../features/brand/BrandDashboard";
import CompanyDashboard from "../../features/company/CompanyDash";
import RegisterCompany from "../../features/company/RegisterCompany";
import VerifyEmailWithOtp from "../../features/company/VerifyEmailWithOtp";
import CompanyDashboardPage from "../../features/dash/CompanyDashboard";
import Dashboard from "../../features/dash/Dashboard";
import DispatchDetails from "../../features/dispatch/DispatchDetails";
import DispatchHistory from "../../features/dispatch/DispatchHistory";
import DispatchImport from "../../features/dispatch/DispatchImport";
import DispatchVehicle from "../../features/dispatch/DispatchVehicle";
import OnGoingTrips from "../../features/dispatch/OnGoingTrips";
import CrewMembers from "../../features/driver/CrewMembers";
import DriverDashboard from "../../features/driver/DriverDashboard";
import DriverDetails from "../../features/driver/DriverDetails";
import ExpenseHeadsDashboard from "../../features/finance/ExpenseHeadsDashboard";
import FinanceDashboard from "../../features/finance/FinanceDashboard";
import FinesDashboard from "../../features/fines/FinesDashboard";
import FuelLogDashboard from "../../features/fuel/FuelLogDashboard";
import IncidentReportDashboard from "../../features/incident/IncidentReportDashboard";
import MaintenanceDashboard from "../../features/maintenance/MaintenanceDashboard";
import PartnerDashboard from "../../features/partner/PartnerDashboard";
import PartnerDetails from "../../features/partner/PartnerDetails";
import RequisitionDash from "../../features/requisition/RequisitionDash";
import CompanySettings from "../../features/settings/CompanySettings";
import Settings from "../../features/settings/Settings";
import StaffDashboard from "../../features/staff/StaffDashboard";
import CustomPricingDashboard from "../../features/subscription/CustomPricingDashboard";
import SubscriptionDashboard from "../../features/subscription/SubscriptionDashboard";
import AuditDashboard from "../../features/transaction/AuditDashboard";
import TransactionDashboard from "../../features/transaction/TransactionDashboard";
import DashUserDetails from "../../features/user/DashUserDetails";
import RoleDetails from "../../features/user/RoleDetails";
import UserDashboard from "../../features/user/UserDashboard";
import VehicleAssignments from "../../features/vehicle/VehicleAssignment";
import VehicleDetails from "../../features/vehicle/VehicleDetails";
import VehiclesDashboard from "../../features/vehicle/VehiclesDashboard";
import ContactUs from "../../pages/ContactUs";
import FAQs from "../../pages/FAQs";
import Features from "../../pages/Features";
import Home from "../../pages/Home";
import Pricing from "../../pages/Pricing";
import PrivacyPolicy from "../../pages/PrivacyPolicy";
import AdminLayout from "../views/layouts/AdminLayout";
import Layout from "../views/layouts/Layout";
import SuperAdminLayout from "../views/layouts/SuperAdminLayout";
import PageUnderConstruction from "../views/PageUnderConstruction";

const guard = (resource: string) => (
  <RequireAuth requiredPermission={resource} requiredAction="View" />
);

const Router: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="features" element={<Features />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="faqs" element={<FAQs />} />
        <Route path="contact" element={<ContactUs />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<PageUnderConstruction />} />
      </Route>

      <Route path="sign-up" element={<RegisterCompany />} />

      <Route path="sign-in" element={<AdminSignIn />} />

      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />

      <Route path="verify-email" element={<VerifyEmailWithOtp />} />

      <Route element={<PersistLogin />}>
        <Route element={<RequireAuth excludedRoles={["SuperAdmin"]} />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route element={guard("Dashboard")}>
              <Route index element={<CompanyDashboardPage />} />
            </Route>

            <Route element={guard("Vehicles")}>
              <Route path="vehicles" element={<VehiclesDashboard />} />
              <Route path="vehicles/:id" element={<VehicleDetails />} />
            </Route>

            <Route element={guard("Dispatch")}>
              <Route path="dispatch/:id" element={<DispatchVehicle />} />
              <Route
                path="dispatch-details/:id"
                element={<DispatchDetails />}
              />
              <Route path="on-going-trips" element={<OnGoingTrips />} />
              <Route path="dispatch-import" element={<DispatchImport />} />
              <Route
                path="dispatch-history/:id"
                element={<DispatchHistory />}
              />
            </Route>

            <Route element={guard("VehicleAssignments")}>
              <Route
                path="vehicle-assignments"
                element={<VehicleAssignments />}
              />
            </Route>

            <Route element={guard("Maintenance")}>
              <Route path="maintenance" element={<MaintenanceDashboard />} />
              <Route path="fuel" element={<FuelLogDashboard />} />
            </Route>

            <Route element={guard("Requisition")}>
              <Route path="requisition" element={<RequisitionDash />} />
            </Route>

            <Route element={guard("Users")}>
              <Route path="users" element={<StaffDashboard />} />
              <Route path="user/:id" element={<DashUserDetails />} />
              <Route path="role/:id" element={<RoleDetails />} />
            </Route>

            <Route element={guard("Company")}>
              <Route path="drivers" element={<DriverDashboard />} />
              <Route path="drivers/:id" element={<DriverDetails />} />
              <Route path="partners" element={<PartnerDashboard />} />
              <Route path="partners/:id" element={<PartnerDetails />} />
              <Route path="crew-members" element={<CrewMembers />} />
              <Route path="fines" element={<FinesDashboard />} />
              <Route
                path="incident-report"
                element={<IncidentReportDashboard />}
              />
              <Route path="finance" element={<FinanceDashboard />} />
              <Route
                path="settings/expense-head"
                element={<ExpenseHeadsDashboard />}
              />
              <Route path="settings" element={<CompanySettings />} />
            </Route>

            <Route
              path="*"
              element={
                <PageUnderConstruction
                  onGoBack={() => navigate(-1)}
                  onGoHome={() => navigate("/admin")}
                />
              }
            />
          </Route>
        </Route>
      </Route>

      <Route element={<PersistLogin />}>
        <Route element={<RequireAuth allowedRoles={["SuperAdmin"]} />}>
          <Route path="/super-admin" element={<SuperAdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="users" element={<UserDashboard />} />
            <Route path="user/:id" element={<DashUserDetails />} />
            <Route path="role/:id" element={<RoleDetails />} />

            <Route path="companies" element={<CompanyDashboard />} />

            <Route path="transaction" element={<TransactionDashboard />} />
            <Route path="vehicle-brands" element={<BrandDashboard />} />

            <Route path="subscription" element={<SubscriptionDashboard />} />

            <Route path="custom-pricing" element={<CustomPricingDashboard />} />

            <Route path="audit-log" element={<AuditDashboard />} />

            <Route path="settings" element={<Settings />} />

            <Route
              path="*"
              element={
                <PageUnderConstruction
                  onGoBack={() => navigate(-1)}
                  onGoHome={() => navigate("/admin")}
                />
              }
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default Router;
